import { CREW_LIBRARY } from './crew';
import { shuffleInPlace } from './rng';
import { ALL_RANKS, ALL_SUITS, } from './types';
const EDICTS = [
    { id: 'cannon', name: 'Cannon Volley', description: 'Steal 6🪙 from opponent immediately. (In solo, +6🪙 from Treasury.)', cost: 5, effect: 'cannon', magnitude: 6 },
    { id: 'repair', name: 'Repair Crew', description: 'Gain 7🪙 from Treasury immediately.', cost: 4, effect: 'repair', magnitude: 7 },
    { id: 'parley', name: 'Parley', description: 'Cancel any cards currently scheduled to go overboard against you next turn.', cost: 3, effect: 'parley', magnitude: 0 },
    { id: 'mark', name: "X Marks the Spot", description: 'Next turn, your highest-value pattern is doubled (earn + steal). Each copy doubles one more.', cost: 6, effect: 'mark', magnitude: 0 },
    { id: 'broadside', name: 'Broadside', description: 'Steal 10🪙 from opponent, then lose 2🪙 from your own hull. (In solo, +10🪙 from Treasury, lose 2🪙.)', cost: 7, effect: 'broadside', magnitude: 10 },
    { id: 'reinforcements', name: 'Reinforcements', description: 'Gain 10🪙 from Treasury. You skip your next turn entirely.', cost: 6, effect: 'reinforcements', magnitude: 10 },
    { id: 'tide_turner', name: 'Tide Turner', description: "Shift up to 10🪙 from the leading captain's hull to the trailing captain's hull (half the gap, capped at 10). Helps only if you're behind. (PvP only.)", cost: 6, effect: 'tide_turner', magnitude: 10 },
    { id: 'kraken_embrace', name: "Kraken's Embrace", description: 'Next turn, EVERY pattern you fire pays double (earn + steal).', cost: 10, effect: 'kraken_embrace', magnitude: 0 },
    { id: 'salvage', name: 'Salvage', description: 'Recover 2 cards from your overboard pile back into your draw pile.', cost: 5, effect: 'salvage', magnitude: 2 },
    { id: 'lightning', name: 'Lightning Strike', description: 'Your first pattern next turn earns +5 bonus (stacks with everything else).', cost: 4, effect: 'lightning', magnitude: 5 },
    // Phase B additions — 4 new edicts
    { id: 'press_crew', name: 'Press Crew', description: "Steal a random crew member from the opponent's tableau. (PvP only — in solo, gain a temporary scoundrel for the rest of the run.)", cost: 6, effect: 'press_crew', magnitude: 1 },
    { id: 'wet_powder', name: 'Wet Powder', description: "Soak the opponent's powder kegs — their next pattern earns and steals 0 (zeroed).", cost: 5, effect: 'wet_powder', magnitude: 0 },
    { id: 'lifeboat', name: 'Lifeboat', description: 'Restore 5🪙 to your hull. You skip your next draw-up (hand stays at current size).', cost: 3, effect: 'lifeboat', magnitude: 5 },
    { id: 'boarding_action', name: 'Boarding Action', description: 'Your next pattern fires twice (the second copy is layered like Kraken, but only on one pattern).', cost: 6, effect: 'boarding_action', magnitude: 0 },
];
function tossAll(rank, cost) {
    return {
        id: `remove-all-${rank}`,
        name: `Toss every ${rank}`,
        description: `Remove ALL ${rank}s from your deck.`,
        cost,
        kind: 'remove-rank',
        rank,
    };
}
const DECK_MODS = [
    { id: 'add-ace', name: 'Forge Ace', description: 'Add an Ace♠ to your deck.', cost: 4, kind: 'add-card', payload: { id: 'forged-A-spades', rank: 'A', suit: 'spades' } },
    { id: 'add-king', name: 'Forge King', description: 'Add a King♥ to your deck.', cost: 5, kind: 'add-card', payload: { id: 'forged-K-hearts', rank: 'K', suit: 'hearts' } },
    { id: 'add-rank-wild', name: 'Forge Joker (Rank-Wild ♠)', description: 'Add a card that matches any rank for patterns. Suit is ♠.', cost: 7, kind: 'add-card', payload: { id: 'forged-rwild-spades', rank: '*', suit: 'spades' } },
    { id: 'add-suit-wild', name: 'Forge Wandering Q (Suit-Wild)', description: 'Add a Queen with no fixed suit — counts as any suit for chains.', cost: 7, kind: 'add-card', payload: { id: 'forged-swild-q', rank: 'Q', suit: '*' } },
    tossAll('2', 5), tossAll('3', 5), tossAll('4', 6), tossAll('5', 6),
    tossAll('6', 7), tossAll('7', 8), tossAll('8', 8), tossAll('9', 9),
    tossAll('10', 9), tossAll('J', 11), tossAll('Q', 12), tossAll('K', 12),
    tossAll('A', 13),
];
// Lookup registry for log annotation — surfaces all named cards/edicts/mods
// so the log UI can wrap them into clickable references.
export function getCardRegistry() {
    const out = [];
    for (const c of Object.values(CREW_LIBRARY)) {
        out.push({ name: c.name, description: c.description, kind: 'crew' });
    }
    for (const e of EDICTS) {
        out.push({ name: e.name, description: e.description, kind: 'edict' });
    }
    for (const m of DECK_MODS) {
        out.push({ name: m.name, description: m.description, kind: 'mod' });
    }
    return out;
}
export function generatePort(state) {
    const rng = state.rngSeed
        ? makeLocalRng(state.rngSeed + state.turnNumber * 17)
        : Math.random;
    // Allow duplicates — same crew can appear repeatedly across shops.
    const crewPool = Object.values(CREW_LIBRARY).slice();
    shuffleInPlace(crewPool, rng);
    const suffix = `-${state.turnNumber}`;
    const offers = [];
    for (let i = 0; i < Math.min(2, crewPool.length); i++) {
        const c = crewPool[i];
        offers.push({ kind: 'crew', id: `${c.id}${suffix}-${i}`, cost: c.cost, data: c });
    }
    const edictPool = EDICTS.slice();
    shuffleInPlace(edictPool, rng);
    if (edictPool[0])
        offers.push({ kind: 'edict', id: `${edictPool[0].id}${suffix}`, cost: edictPool[0].cost, data: edictPool[0] });
    const modPool = DECK_MODS.slice();
    shuffleInPlace(modPool, rng);
    if (modPool[0])
        offers.push({ kind: 'mod', id: `${modPool[0].id}${suffix}`, cost: modPool[0].cost, data: modPool[0] });
    // Less-money captain picks first. Tiebreak: current turn player.
    const a = state.players.a.doubloons;
    const b = state.players.b.doubloons;
    const first = a < b ? 'a' : b < a ? 'b' : state.turnOf;
    const second = first === 'a' ? 'b' : 'a';
    return { offers, pickOrder: [first, second], currentPick: 0 };
}
export function applyPurchase(state, buyer, offerId) {
    const player = state.players[buyer];
    if (!state.shop)
        return { ok: false, reason: 'no shop' };
    const idx = state.shop.offers.findIndex((o) => o.id === offerId);
    if (idx < 0)
        return { ok: false, reason: 'offer gone' };
    const offer = state.shop.offers[idx];
    if (player.doubloons - offer.cost <= 0) {
        return { ok: false, reason: 'cannot afford (would sink you)' };
    }
    const start = player.doubloons;
    const opp = state.players[buyer === 'a' ? 'b' : 'a'];
    const oppStart = opp.doubloons;
    player.doubloons -= offer.cost;
    const name = offer.data.name ?? '(item)';
    if (offer.kind === 'crew') {
        player.crew.push(offer.data);
    }
    else if (offer.kind === 'edict') {
        applyEdictNow(state, buyer, offer.data);
    }
    else if (offer.kind === 'mod') {
        applyModNow(state, player, offer.data, offer.id);
    }
    // Mark as purchased instead of removing — keeps offer slots in place.
    offer.purchased = true;
    let text = `${player.name} bought ${name} for ${offer.cost}🪙 (🪙${start}→${player.doubloons}`;
    if (opp.doubloons !== oppStart) {
        text += `; ${opp.name}: 🪙${oppStart}→${opp.doubloons}`;
    }
    text += ')';
    state.log.push({ turn: state.turnNumber, player: buyer, text });
    return { ok: true };
}
function applyEdictNow(state, buyer, edict) {
    const me = state.players[buyer];
    const them = state.players[buyer === 'a' ? 'b' : 'a'];
    const isPve = state.mode === 'pve';
    if (edict.effect === 'cannon') {
        if (isPve) {
            me.doubloons += edict.magnitude;
        }
        else {
            const taken = Math.min(edict.magnitude, Math.max(them.doubloons, 0));
            them.doubloons -= taken;
            me.doubloons += taken;
        }
    }
    else if (edict.effect === 'repair') {
        me.doubloons += edict.magnitude;
    }
    else if (edict.effect === 'parley') {
        me.pendingDiscards = 0;
    }
    else if (edict.effect === 'mark') {
        me.pendingMarks = (me.pendingMarks ?? 0) + 1;
    }
    else if (edict.effect === 'broadside') {
        if (isPve) {
            me.doubloons += edict.magnitude;
        }
        else {
            const taken = Math.min(edict.magnitude, Math.max(them.doubloons, 0));
            them.doubloons -= taken;
            me.doubloons += taken;
        }
        me.doubloons -= 2;
    }
    else if (edict.effect === 'reinforcements') {
        me.doubloons += edict.magnitude;
        me.skipNextTurn = true;
    }
    else if (edict.effect === 'tide_turner') {
        if (!isPve) {
            const cap = edict.magnitude ?? 10;
            let leader = null;
            let trailer = null;
            if (me.doubloons > them.doubloons) {
                leader = me;
                trailer = them;
            }
            else if (them.doubloons > me.doubloons) {
                leader = them;
                trailer = me;
            }
            if (leader && trailer) {
                const gap = leader.doubloons - trailer.doubloons;
                const shift = Math.min(Math.floor(gap / 2), cap);
                leader.doubloons -= shift;
                trailer.doubloons += shift;
            }
        }
    }
    else if (edict.effect === 'kraken_embrace') {
        me.pendingDoubleAllPatterns = true;
    }
    else if (edict.effect === 'lightning') {
        me.pendingPatternBonus = (me.pendingPatternBonus ?? 0) + edict.magnitude;
    }
    else if (edict.effect === 'salvage') {
        const ob = me.overboard ?? [];
        const moved = ob.slice(0, edict.magnitude);
        if (moved.length > 0) {
            me.overboard = ob.slice(edict.magnitude);
            me.draw = me.draw.concat(moved);
            const rng = makeLocalRng((state.rngSeed ?? 0) + state.turnNumber * 53 + 7);
            shuffleInPlace(me.draw, rng);
        }
    }
    else if (edict.effect === 'press_crew') {
        if (!isPve && them.crew.length > 0) {
            const rng = makeLocalRng((state.rngSeed ?? 0) + state.turnNumber * 53 + 13);
            const idx = Math.floor(rng() * them.crew.length);
            const stolen = them.crew.splice(idx, 1)[0];
            me.crew.push(stolen);
        }
        else if (isPve) {
            // Solo: spawn a temporary low-tier scoundrel — give a Bosun copy.
            const bosun = CREW_LIBRARY.bosun;
            if (bosun)
                me.crew.push({ ...bosun });
        }
    }
    else if (edict.effect === 'wet_powder') {
        them.pendingZeroPattern = true;
    }
    else if (edict.effect === 'lifeboat') {
        me.doubloons += edict.magnitude;
        // Set pendingHandSize to current hand length so the next draw-up doesn't
        // refill — they keep what they have. Engine clears this after one cycle.
        me.pendingHandSize = me.hand.length;
    }
    else if (edict.effect === 'boarding_action') {
        me.pendingCopyNextPattern = true;
    }
}
function applyModNow(state, player, mod, offerId) {
    if (mod.kind === 'add-card' && mod.payload) {
        // Deterministic suffix derived from match seed + offer id, mirroring the
        // Python engine. Keeps client-side optimistic IDs aligned with the
        // server's authoritative IDs so card selection state survives a refresh.
        const suffix = djb2Hex(`${state.rngSeed ?? 0}|${offerId}`).slice(0, 6);
        const card = { ...mod.payload, id: `${mod.payload.id}-${suffix}` };
        player.draw.push(card);
    }
    else if (mod.kind === 'remove-rank' && mod.rank) {
        const target = mod.rank;
        player.draw = player.draw.filter((c) => c.rank !== target);
        player.discard = player.discard.filter((c) => c.rank !== target);
        player.hand = player.hand.filter((c) => c.rank !== target);
    }
    else if (mod.kind === 'remove-card') {
        const targetRank = mod.id === 'remove-low' ? '2' : '3';
        for (const pile of [player.draw, player.discard, player.hand]) {
            const idx = pile.findIndex((c) => c.rank === targetRank);
            if (idx >= 0) {
                pile.splice(idx, 1);
                return;
            }
        }
    }
}
export function sellCrew(state, seller, crewId) {
    const player = state.players[seller];
    const idx = player.crew.findIndex((c) => c.id === crewId);
    if (idx < 0)
        return { ok: false, reason: "you don't own that crew" };
    const crew = player.crew[idx];
    const refund = Math.floor((crew.cost * 2) / 3);
    const start = player.doubloons;
    player.crew.splice(idx, 1);
    player.doubloons += refund;
    state.log.push({
        turn: state.turnNumber,
        player: seller,
        text: `${player.name} sold ${crew.name} for ${refund}🪙 (🪙${start}→${player.doubloons})`,
    });
    return { ok: true, refund };
}
export function passShop(state) {
    if (!state.shop)
        return;
    state.shop.currentPick += 1;
}
export function shopFinished(state) {
    if (!state.shop)
        return true;
    const buyable = state.shop.offers.filter((o) => !o.purchased).length;
    return state.shop.currentPick >= state.shop.pickOrder.length || buyable === 0;
}
export function debugRanks() {
    return ALL_RANKS;
}
export function debugSuits() {
    return ALL_SUITS;
}
function djb2Hex(s) {
    let h = 5381 >>> 0;
    for (let i = 0; i < s.length; i++) {
        h = (Math.imul(h, 33) + s.charCodeAt(i)) >>> 0;
    }
    return h.toString(16).padStart(8, '0');
}
function makeLocalRng(seed) {
    let s = seed >>> 0;
    return () => {
        s = (s + 0x6d2b79f5) >>> 0;
        let t = s;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
